import { useState, useEffect, useRef } from "react";
import useDeepCompareEffect from 'use-deep-compare-effect'
import {
    QuerySnapshotOptions,
    QuerySnapshotResponse,
    querySnapshot,
    QueryTransactionsOptions,
    QueryTransactionsResponse,
    queryTransactions,
    ManagedProductResponse,
    getProductLedgerManagedStatus,
    querySnapshotByProduct,
    QuerySnapshotByProductOptions,
    QuerySnapshotByProductResponse
} from "./allocation-service";

export type ApiCallState<TResponse> = {
    loading: boolean;
    error: Error | null;
    data: TResponse | null;
    refresh?: () => void | undefined;
};

export function useQuerySnapshot(options: QuerySnapshotOptions) {
    const [state, setState] = useState<ApiCallState<QuerySnapshotResponse>>({
        data: null as any,
        error: null as any,
        loading: false,
    });
    const [refreshIndex, setRefreshIndex] = useState(0);

    useDeepCompareEffect(() => {
        setState({ data: null as any, error: null as any, loading: true });
        (async () => {
            try {
                const data = await querySnapshot(options);
                setState({ data, error: null, loading: false });
            } catch (error) {
                setState({ data: null, error, loading: false });
            }
        })();
    }, [refreshIndex, options]);

    return {
        ...state,
        refresh: () => setRefreshIndex(refreshIndex + 1),
    };
}

export function useQuerySnapshotByProduct(options: QuerySnapshotByProductOptions) {
    const [state, setState] = useState<ApiCallState<QuerySnapshotByProductResponse>>({
        data: null as any,
        error: null as any,
        loading: false,
    });
    const [refreshIndex, setRefreshIndex] = useState(0);

    useEffect(() => {
        setState({ data: null as any, error: null as any, loading: true });
        (async () => {
            try {
                const data = await querySnapshotByProduct(options);
                setState({ data, error: null, loading: false });
            } catch (error : any) {
                setState({ data: null, error, loading: false });
            }
        })();
    }, [refreshIndex, options]);

    return {
        ...state,
        refresh: () => setRefreshIndex(refreshIndex + 1),
    };
}

export function useQueryTransactions(options: QueryTransactionsOptions) {
    const [state, setState] = useState<ApiCallState<QueryTransactionsResponse>>({
        data: null as any,
        error: null as any,
        loading: false,
    });
    const [refreshIndex, setRefreshIndex] = useState(0);

    useDeepCompareEffect(() => {
        setState({ data: null as any, error: null as any, loading: true });
        (async () => {
            try {
                const data = await queryTransactions(options);

                setState({ data, error: null, loading: false });
            } catch (error) {
                setState({ data: null, error, loading: false });
            }
        })();
    }, [refreshIndex, options]);

    return {
        ...state,
        refresh: () => setRefreshIndex(refreshIndex + 1),
    };
}

export function useGetProductPartitionManagedStatus(productId: number) {
    const [state, setState] = useState<ApiCallState<ManagedProductResponse>>({
        data: null,
        error: null,
        loading: false,
    });
    const [refreshIndex, setRefreshIndex] = useState(0);
    const mounted = useRef(false);

    useEffect(() => {
        mounted.current = true;
        setState({ data: null, error: null, loading: true });
        (async () => {
            try {
                const data = await getProductLedgerManagedStatus(productId);
                if (mounted.current) 
                    setState({ data, error: null, loading: false });
            } catch (error: any) {
                if (mounted.current) 
                    setState({ data: null, error, loading: false });
            }
        })();
        return () => {
            mounted.current = false;
        };
    }, [refreshIndex, productId]);

    return {
        ...state,
        refresh: () => setRefreshIndex(refreshIndex + 1),
    };
}
